import { Helmet } from 'react-helmet-async';
import { useLayoutEffect } from 'react';
import { useLocation } from '@/lib/router-compat';
import { DEFAULT_PAGE_TITLE } from '@/lib/seoDefaults';
import { useRouter } from '@tanstack/react-router';

/**
 * Head tags on React 19.
 *
 * On React 18, Helmet owned <head>: every tag it wrote carried data-rh="true",
 * and it replaced all data-rh tags whenever a page rendered <SEO>. index.html's
 * crawler fallbacks are marked data-rh for that reason, and so are the tags in
 * every prerendered snapshot (scripts/prerender.mjs saves the live DOM; the
 * homepage snapshot is also dist/index.html, the shell for every other route).
 *
 * On React 19, react-helmet-async 3 only renders plain elements and React
 * hoists them into <head>. React removes nothing it did not create, so the
 * fallbacks and any snapshot's tags would stay next to the page's own: two
 * descriptions (site review F1) and the homepage's canonical on every route.
 *
 * So <SEO> keeps the data-rh contract itself: its tags carry data-rh="true",
 * and before rendering it removes every data-rh tag in <head> that React does
 * not own. "Owned" means present right after one of our commits: by then the
 * removal has already run, so the only data-rh tags left are React's.
 *
 * The removal runs during render, not in an effect: at commit React adopts an
 * existing tag whose attributes match one it is about to insert (e.g.
 * author="AKIVA"), and removing it afterwards would detach a node React
 * owns. Removing foreign nodes is idempotent, so a repeated or discarded
 * render is harmless.
 */
const reactOwnedHeadTags = new WeakSet<Element>();

function managedHeadTags(): Element[] {
  if (typeof document === 'undefined') return [];
  return Array.from(document.head.querySelectorAll('[data-rh="true"]'));
}

function releaseForeignHeadTags() {
  for (const el of managedHeadTags()) {
    if (!reactOwnedHeadTags.has(el)) el.remove();
  }
}

function recordReactHeadTags() {
  for (const el of managedHeadTags()) reactOwnedHeadTags.add(el);
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  robots?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  structuredData?: object;
}

const SEO = ({
  title = DEFAULT_PAGE_TITLE,
  description = 'AKIVA delivers enterprise AI solutions including machine learning, multi-agent systems, MLOps, and strategy consulting for government and commercial clients.',
  keywords = 'AI engineering, machine learning, MLOps, multi-agent systems, AI consulting, government AI, enterprise AI',
  robots = 'index, follow',
  canonicalUrl,
  ogImage = '/og-image.jpg',
  ogType = 'website',
  structuredData,
}: SEOProps) => {
  releaseForeignHeadTags();
  useLayoutEffect(recordReactHeadTags);
  const siteUrl = 'https://akiva.com';
  const location = useLocation();
  const router = useRouter({ warn: false });
  // Route heads survive SSR auth/loading gates. Avoid two conflicting owners
  // of descriptions and canonical tags during hydration and navigation.
  const routeOwnsHead = router?.state.matches.some(match => (match.meta as { name?: string }[] | undefined)?.some(m => !!m && 'name' in m && m.name === 'description')) ?? false;
  const resolvedCanonical = canonicalUrl ?? (location.pathname.replace(/\/+$/, '') || '/');
  const canonicalHref = new URL(resolvedCanonical, siteUrl).toString();
  const imageHref = new URL(ogImage, siteUrl).toString();
  const fullTitle = title.includes('AKIVA') ? title : `${title} | AKIVA`;

  const organization = {
    '@type': 'Organization',
    name: 'AKIVA',
    url: siteUrl,
    logo: `${siteUrl}/akiva-logo.png`,
    sameAs: [
      'https://www.linkedin.com/company/akiva-ai',
      'https://github.com/akiva-ai',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@akiva.com',
      contactType: 'customer service',
    },
  };

  // Service pages describe a service; product marketing pages describe a page.
  // Product rich results require a real offer, review or aggregate rating, which
  // these quote/demo and category pages do not publish. A page with those facts
  // can opt in explicitly through `structuredData`.
  const cleanTitle = title.replace(/\s*\|\s*AKIVA(\s+AI)?$/i, '').trim();
  const pathname = location.pathname;
  let autoSchema: object;
  if (pathname.startsWith('/service/')) {
    autoSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: cleanTitle,
      description,
      url: canonicalHref,
      provider: organization,
      areaServed: { '@type': 'Country', name: 'United States' },
      serviceType: 'AI Engineering & Strategy Consulting',
    };
  } else if (pathname.startsWith('/products/')) {
    autoSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: cleanTitle,
      description,
      url: canonicalHref,
      publisher: organization,
    };
  } else {
    autoSchema = {
      '@context': 'https://schema.org',
      ...organization,
      description,
    };
  }

  const defaultStructuredData = autoSchema;

  return (
    <Helmet>
      {!routeOwnsHead && [
      <title>{fullTitle}</title>,
      <meta data-rh="true" name="description" content={description} />,
      <meta data-rh="true" name="keywords" content={keywords} />,
      <meta data-rh="true" name="robots" content={robots} />,
      <meta data-rh="true" name="author" content="AKIVA" />,

      <link data-rh="true" rel="canonical" href={canonicalHref} />,

      <meta data-rh="true" property="og:title" content={fullTitle} />,
      <meta data-rh="true" property="og:description" content={description} />,
      <meta data-rh="true" property="og:type" content={ogType} />,
      <meta data-rh="true" property="og:site_name" content="AKIVA" />,
      <meta data-rh="true" property="og:url" content={canonicalHref} />,

      <meta data-rh="true" name="twitter:card" content="summary_large_image" />,
      <meta data-rh="true" name="twitter:title" content={fullTitle} />,
      <meta data-rh="true" name="twitter:description" content={description} />,
      ]}
      {!routeOwnsHead && [
        <meta data-rh="true" property="og:image" content={imageHref} />,
        <meta data-rh="true" name="twitter:image" content={imageHref} />,
      ]}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
